import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { MarketDialogueAnswerType, type MarketDialogueQuestionDto } from "../../autogen/bff-api";
import { TextLengthHint } from "../../common/input/TextLengthHint";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import {
  answerTypeOf,
  MARKET_DIALOGUE_ANSWER_TYPES,
  takesDocuments,
  useAnswerTypeLabel,
  useCommentSubjectKindLabel,
} from "./marketDialogueLabels";
import { isQuestionTooLong, MAX_QUESTION_LENGTH, MAX_QUESTIONS } from "./questionLimits";

export const MarketDialogueQuestionList = ({ questions }: { questions: MarketDialogueQuestionDto[] }) => {
  const { t } = useTranslation();
  const answerTypeLabel = useAnswerTypeLabel();
  const subjectKindLabel = useCommentSubjectKindLabel();
  if (questions.length === 0) return <Text color="smMuted">{t("No questions yet.")}</Text>;
  return (
    <Stack as="ol" spacing="2" pl="5">
      {questions.map((question, index) => (
        <Box as="li" key={question.id ?? index}>
          <Text whiteSpace="pre-wrap">{question.text}</Text>
          <HStack spacing="2" mt="1">
            {question.subject && <Badge colorScheme="teal">{subjectKindLabel(question.subject.kind)}</Badge>}
            <Badge variant="subtle">{answerTypeLabel(answerTypeOf(question))}</Badge>
            {answerTypeOf(question) !== MarketDialogueAnswerType.Document && takesDocuments(question) && (
              <Badge variant="subtle">{t("Documents may be attached")}</Badge>
            )}
          </HStack>
        </Box>
      ))}
    </Stack>
  );
};

interface Props {
  questions: MarketDialogueQuestionDto[];
  onChange: (questions: MarketDialogueQuestionDto[]) => void;
}

export const MarketDialogueQuestions = ({ questions, onChange }: Props) => {
  const { t } = useTranslation();
  const answerTypeLabel = useAnswerTypeLabel();
  const subjectKindLabel = useCommentSubjectKindLabel();
  const ownQuestions = questions.filter((question) => !question.subject).length;
  const confirmation = useConfirmationModal();
  const removeAt = (index: number) => onChange(questions.filter((_, i) => i !== index));
  const confirmRemovingItem = (index: number, item: string) =>
    confirmation.show({
      title: t("Remove «{{item}}» from the round?", { item }),
      description: t(
        "It cannot be added back to this round. To include it again, delete the draft round and send the procurement out for comment again.",
      ),
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
      onConfirmed: () => removeAt(index),
    });

  const changeAt = (index: number, change: Partial<MarketDialogueQuestionDto>) =>
    onChange(questions.map((question, i) => (i === index ? { ...question, ...change } : question)));

  return (
    <Stack spacing="3">
      {confirmation.modal}
      {questions.map((question, index) =>
        question.subject ? (
          <Box
            key={question.id ?? `subject-${index}`}
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="md"
            p="3"
            data-testid="comment-subject-item"
          >
            <Flex gap="2" align="flex-start" justify="space-between">
              <Box minW="0">
                <Badge colorScheme="teal" mb="1">
                  {subjectKindLabel(question.subject.kind)}
                </Badge>
                <Text whiteSpace="pre-wrap">{question.text}</Text>
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Taken from the procurement as it was sent. Suppliers comment on it.")}
                </Text>
              </Box>
              <IconButton
                aria-label={t("Remove {{item}} from the round", { item: question.text })}
                icon={<Icon as={FiTrash2} />}
                variant="ghost"
                onClick={() => confirmRemovingItem(index, question.text)}
              />
            </Flex>
          </Box>
        ) : (
        <FormControl key={question.id ?? `new-${index}`} isInvalid={isQuestionTooLong(question)}>
          <FormLabel fontSize="sm">{t("Question {{number}}", { number: index + 1 })}</FormLabel>
          <Flex gap="2" align="flex-start">
            <Textarea
              value={question.text}
              onChange={(e) => changeAt(index, { text: e.target.value })}
              placeholder={t("What would you like the suppliers to answer?") ?? undefined}
              maxLength={MAX_QUESTION_LENGTH}
              rows={2}
            />
            <IconButton
              aria-label={t("Remove question")}
              icon={<Icon as={FiTrash2} />}
              variant="ghost"
              onClick={() => removeAt(index)}
            />
          </Flex>
          <TextLengthHint text={question.text} max={MAX_QUESTION_LENGTH} />
          <Flex gap="4" mt="2" align="center" wrap="wrap">
            <Select
              aria-label={t("Answer type for question {{number}}", { number: index + 1 }) ?? undefined}
              size="sm"
              maxW="56"
              value={answerTypeOf(question)}
              onChange={(e) => changeAt(index, { answerType: e.target.value as MarketDialogueAnswerType })}
            >
              {MARKET_DIALOGUE_ANSWER_TYPES.map((answerType) => (
                <option key={answerType} value={answerType}>
                  {answerTypeLabel(answerType)}
                </option>
              ))}
            </Select>
            {answerTypeOf(question) !== MarketDialogueAnswerType.Document && (
              <Switch
                size="sm"
                fontSize="sm"
                display="inline-flex"
                alignItems="flex-start"
                lineHeight="short"
                minW="0"
                isChecked={question.allowsAttachments ?? true}
                onChange={(e) => changeAt(index, { allowsAttachments: e.target.checked })}
              >
                {t("The supplier may attach documents")}
              </Switch>
            )}
          </Flex>
        </FormControl>
        ),
      )}
      {ownQuestions < MAX_QUESTIONS ? (
        <Button
          alignSelf="flex-start"
          variant="outline"
          leftIcon={<Icon as={FiPlus} />}
          onClick={() =>
            onChange([...questions, { text: "", answerType: MarketDialogueAnswerType.FreeText, allowsAttachments: true }])
          }
        >
          {t("Add question")}
        </Button>
      ) : (
        <Text fontSize="sm" color="smMuted">
          {t("A market dialogue can have at most {{max}} questions.", { max: MAX_QUESTIONS })}
        </Text>
      )}
    </Stack>
  );
};

